import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@shadcn/ui/lib/utils';
import { ActionCard } from './action-card/action-card';
import { useQuery } from '@apollo/client';
import { addHours, addMinutes, subDays } from 'date-fns';
import {
    GET_CONTACT_CLASSIFICATION,
    GET_CONTACTS_INTERACTIONS,
    GET_CONTACTS_WEIGHTED_SCORES,
    GET_TOTAL_NUMBER_OF_CONTACTS,
    GetContactsWeightedScoresQueryVariables,
    GetContactsWeightedScoresQuery,
} from '@zaplify/graphql';
import {
    ContactClassificationMetadata,
    DEFAULT_FILTER_BOOSTS,
    mergeWeights,
    transformWeightsForQuery,
    UserObjectives,
    WeightKeys,
} from '@andsend/intelligence-shared';
import { ScrollArea } from '@shadcn/ui/components/ui/scroll-area';
import { useSnoozeDialogV2 } from '../../../../components/dialogs/snooze-dialog-v2';
import { AnimatePresence, motion } from 'motion/react';
import { debounce } from 'lodash';
import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@shadcn/ui/components/ui/dialog';
import { Accordion, AccordionContent, AccordionTrigger, AccordionItem } from '@shadcn/ui/components/ui/accordion';
import { useGqlQueryVariables } from '../../hooks/use-gql-query-variables';
import { AgentFilterCard } from './agent-filter-card';
import { Button } from '@shadcn/ui/components/ui/button';
import { Card } from '@shadcn/ui/components/ui/card';
import { ContactSyncInProgressCard } from './contact-sync-in-progress-card';
import { useUser } from '../../../../hooks/use-user';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

// Constants - moved outside component to prevent recreation
const DEFAULT_LIMIT = 10;
const CONDENSED_WIDTH_THRESHOLD = 500;

export const ActionCardList: FC<{ className?: string }> = ({ className }) => {
    const [activeFilter, setActiveFilter] = useState<WeightKeys | 'urgent' | undefined>(undefined);
    const user = useUser();
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        gqlQueryVariables,
        filterContactIds,
        filterGroupIds,
        textSearchQuery,
        setGqlQueryVariables,
        setFilterContactIds,
        setUserRequest,
    } = useGqlQueryVariables();
    const feedContainerRef = useRef<HTMLDivElement>(null);
    const feedViewportRef = useRef<HTMLDivElement>(null);
    const { openDialogForContact: openClassificationDialogForContact, ClassificationDialogComponent } =
        useClassificationDialog();
    const [contactsOutsideFilterCount, setContactsOutsideFilterCount] = useState<number>(0);

    const { data: interactionsData } = useQuery(GET_CONTACTS_INTERACTIONS);
    // Track if a fetchMore is in progress
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    // Track container width to determine if cards should be condensed
    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [isCondensed, setIsCondensed] = useState<boolean>(false);
    const [totalContactsCount, setTotalContactsCount] = useState<number>(100);

    const { data: totalContactsCountData } = useQuery(GET_TOTAL_NUMBER_OF_CONTACTS, {
        pollInterval: totalContactsCount < 20 ? 60000 : undefined,
    });

    useEffect(() => {
        if (totalContactsCountData) {
            setTotalContactsCount(totalContactsCountData.Contacts_aggregate.aggregate.count);
        }
    }, [totalContactsCountData]);

    // Constants - using useMemo for future database integration
    const ORGANIZATION_DEFAULTS = useMemo<UserObjectives>(
        () => ({ networking: 1.0, sales: 1.0 }),
        [] // Empty dependency array since these will eventually come from DB
    );

    const USER_SETTINGS = useMemo<UserObjectives>(() => ({ sales: 1.0, networking: 1.0 }), []);

    const CHAT_OVERRIDES = useMemo<UserObjectives>(() => ({ fundraising: 1.0 }), []);

    const HEURISTIC_BOOST = useMemo<UserObjectives>(() => ({ customerSuccess: 1.0 }), []);

    // Base weights - stable, never changes
    const baseWeights = useMemo(
        () => mergeWeights(ORGANIZATION_DEFAULTS, USER_SETTINGS, CHAT_OVERRIDES, HEURISTIC_BOOST),
        [] // Empty dependency array since all values are constants
    );

    const interactionsWeights = useMemo(() => {
        if (!interactionsData?.notInterestedInteractions && !interactionsData?.otherInteractions) return {};

        // Combine both arrays of interactions
        const allInteractions = [
            ...(interactionsData?.notInterestedInteractions || []),
            ...(interactionsData?.otherInteractions || []),
        ];

        const weights = allInteractions.map((interaction) => {
            return interaction?.weights as ContactClassificationMetadata;
        });

        const objectiveScoresSum = { ...baseWeights };

        weights.forEach((weight) => {
            const objectiveScores = weight?.objectiveScores;
            if (!objectiveScores) return;
            Object.entries(objectiveScores).forEach(([key, value]) => {
                objectiveScoresSum[key as keyof ContactClassificationMetadata['objectiveScores']] += value;
            });
        });

        // Now we want to normalize the objective scores so that the max value is at least 1 and at most 2.
        const maxValue = Math.min(2, Math.max(...Object.values(objectiveScoresSum)));
        if (maxValue < 1) {
            const isNegative = maxValue < 0;

            const normalizedObjectiveScores = Object.fromEntries(
                Object.entries(objectiveScoresSum).map(([key, value]) => [
                    key,
                    isNegative ? maxValue / value : value / maxValue,
                ])
            );
            return normalizedObjectiveScores;
        }

        return objectiveScoresSum;
    }, [interactionsData]);

    const filterAdjustedWeights = useMemo(() => {
        const result = mergeWeights(interactionsWeights, DEFAULT_FILTER_BOOSTS[activeFilter]);

        console.log('Filter weights recalculated:', {
            activeFilter,
            filterBoost: DEFAULT_FILTER_BOOSTS[activeFilter],
            baseWeights,
            interactionsWeights,
            result,
        });

        return result;
    }, [baseWeights, activeFilter, baseWeights, interactionsWeights]);

    const isOnChatRoute = location.pathname.includes('/actions-v2/chat');

    const queryVariables = useMemo(() => {
        const transformedWeights = transformWeightsForQuery(filterAdjustedWeights);

        const variables: GetContactsWeightedScoresQueryVariables = gqlQueryVariables || {
            ...transformedWeights,
            where: {
                _and: [
                    {
                        _or: [
                            { lastCompletedActionAt: { _is_null: true } },
                            { lastCompletedActionAt: { _lt: addMinutes(new Date(), -15) } },
                        ],
                    },
                    {
                        _or: [{ snoozedUntil: { _lt: addHours(new Date(), 8) } }, { snoozedUntil: { _is_null: true } }],
                    },
                    filterGroupIds?.[0] ? { _GroupToContacts: { B: { _eq: filterGroupIds[0] } } } : {},
                    // TODO: remove when core problem addressed
                    //  Temporary fix to make sure cold outreach messages aren't appearing in the action list
                    {
                        _not: {
                            _and: [
                                { metadata: { _contains: { contactType: 'SELLER' } } },
                                { metadata: { _contains: { responsePattern: 'NONE' } } },
                            ],
                        },
                    },
                ],
                compositeScore: { _gte: 40 },
                userSetAsNotInterestedAt: { _is_null: true },
            },
        };
        if ((filterContactIds?.length || 0) > 0) {
            // hard override the query variables to only include the filter contact ids
            variables.where = {
                id: { _in: filterContactIds },
            };
        }

        if (textSearchQuery) {
            variables.where = {
                _or: [
                    { fullName: { _ilike: `%${textSearchQuery}%` } },
                    { occupationTitle: { _ilike: `%${textSearchQuery}%` } },
                    { organizationName: { _ilike: `%${textSearchQuery}%` } },
                ],
            };
        }

        variables.limit = DEFAULT_LIMIT;
        variables.offset = 0;
        console.log('gqlQueryVariables', gqlQueryVariables);
        console.log('queryVariables', variables);
        return variables;
    }, [filterAdjustedWeights, gqlQueryVariables, filterContactIds, textSearchQuery, filterGroupIds]);

    const [contactsToDisplay, setContactsToDisplay] = useState<
        GetContactsWeightedScoresQuery['contacts_weighted_scores']
    >([]);

    // Main query for filtered and weighted contacts
    const { data, loading, error, fetchMore } = useQuery(GET_CONTACTS_WEIGHTED_SCORES, {
        variables: queryVariables,
        errorPolicy: 'all',
        pollInterval: contactsToDisplay.length < 7 ? 20 * 1000 : undefined,
        onCompleted: (data) => {
            console.log('Query completed with results:', {
                queryVariables: queryVariables,
                contactCount: data?.contacts_weighted_scores?.length,
                totalCount: data?.total_contacts?.aggregate?.count,
                filteredCount: data?.filtered_contacts_count?.aggregate?.count,
            });

            // Calculate contacts outside filter
            const totalCount = data?.total_contacts?.aggregate?.count || 0;
            const filteredCount = data?.filtered_contacts_count?.aggregate?.count || 0;

            // If we're using specific contact IDs, we need to calculate differently
            if ((filterContactIds?.length || 0) > 0) {
                setContactsOutsideFilterCount(totalCount - filterContactIds.length);
            } else {
                // For agent-generated filters or default filters
                setContactsOutsideFilterCount(totalCount - filteredCount);
            }
        },
    });

    useEffect(() => {
        //
        setContactsToDisplay(data?.contacts_weighted_scores || []);
    }, [data]);

    const shouldDisplaySyncingCard =
        user.createdAt > dayjs().subtract(30, 'minutes').toDate() ||
        (totalContactsCount < 50 && contactsToDisplay.length < 10);

    const { SnoozeDialogV2Component, openDialogForContact } = useSnoozeDialogV2();

    // Optionally, track if there are more items to fetch
    // If your backend returns total count, you can use it here
    // For now, we'll assume if we get less than limit, there's no more
    const hasMore = data?.filtered_contacts_count?.aggregate?.count > contactsToDisplay.length && !loading;

    const handleClearFilter = () => {
        const variables = queryVariables;
        const where = {
            _not: variables.where,
        };
        setGqlQueryVariables({ ...variables, where });
        setFilterContactIds(undefined);
        setUserRequest('Low priority actions');
    };

    // Setup ResizeObserver to track container width
    useEffect(() => {
        const container = feedContainerRef.current;
        if (!container) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width } = entry.contentRect;
                setContainerWidth(width);
                setIsCondensed(width < CONDENSED_WIDTH_THRESHOLD);
            }
        });

        resizeObserver.observe(container);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        const viewport = feedViewportRef.current;
        if (!viewport) return;

        const handleScroll = debounce(() => {
            const { scrollTop, scrollHeight, clientHeight } = viewport;
            const isNearBottom = scrollHeight - scrollTop - clientHeight < 400;

            if (isNearBottom && hasMore && !isFetchingMore && !loading) {
                console.log('fetching more');
                setIsFetchingMore(true);
                fetchMore({
                    variables: {
                        ...queryVariables,
                        offset: contactsToDisplay.length,
                    },
                }).finally(() => setIsFetchingMore(false));
            }
        }, 200);

        viewport.addEventListener('scroll', handleScroll);
        return () => {
            viewport.removeEventListener('scroll', handleScroll);
            handleScroll.cancel && handleScroll.cancel();
        };
    }, [contactsToDisplay.length, hasMore, isFetchingMore, loading, fetchMore, queryVariables]);

    // Read contactIds from URL params and set them as filter
    useEffect(() => {
        const contactIdsParam = searchParams.get('contactIds');
        if (contactIdsParam && !filterContactIds?.length) {
            const contactIds = contactIdsParam.split(',').filter(Boolean);
            if (contactIds.length > 0) {
                setFilterContactIds(contactIds);
                setUserRequest('Network job changes - Recent promotions and new roles');

                // Remove the searchParam after setting the filter
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('contactIds');
                setSearchParams(newSearchParams);
            }
        }
    }, [searchParams, filterContactIds, setFilterContactIds, setUserRequest, setSearchParams]);

    return (
        <ScrollArea
            className={cn(
                'h-[calc(100dvh-6rem)] w-full',
                {
                    'h-[calc(100dvh-8rem)]': !isOnChatRoute,
                },
                className,
                'overflow-y-auto'
            )}
            ref={feedContainerRef}
            viewportRef={feedViewportRef}
        >
            <div className="flex flex-col gap-4 p-4 items-center justify-center w-full">
                <AnimatePresence mode="popLayout">
                    {shouldDisplaySyncingCard && <ContactSyncInProgressCard />}
                    {(gqlQueryVariables || filterContactIds?.[0] || textSearchQuery || filterGroupIds?.[0]) && (
                        <motion.div
                            key={JSON.stringify(gqlQueryVariables || filterContactIds?.[0] || textSearchQuery)}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: -100 }}
                            transition={{
                                duration: 0.2,
                                layout: { type: 'spring', stiffness: 300, damping: 30 },
                            }}
                            className="w-full max-w-[min(70dvw,60rem)]"
                            layout
                        >
                            <AgentFilterCard
                                className="max-w-[min(70dvw,60rem)]"
                                resultCount={data?.filtered_contacts_count?.aggregate?.count || 0}
                            />
                        </motion.div>
                    )}
                    {contactsToDisplay.length > 0 &&
                        contactsToDisplay.map((contact) => (
                            <motion.div
                                key={contact.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, x: -100 }}
                                transition={{
                                    duration: 0.2,
                                    layout: { type: 'spring', stiffness: 300, damping: 30 },
                                }}
                                className="w-full max-w-[min(70dvw,60rem)]"
                                layout="position"
                            >
                                <ActionCard
                                    key={contact.id}
                                    contact={contact}
                                    score={contact.compositeScore}
                                    openSnoozeDialogForContact={openDialogForContact}
                                    openClassificationDialogForContact={openClassificationDialogForContact}
                                    condensed={isCondensed}
                                />
                            </motion.div>
                        ))}
                    {contactsToDisplay.length === 0 && !loading && !isFetchingMore && !shouldDisplaySyncingCard && (
                        <motion.div key="no-actions" className="flex w-full h-20 items-center justify-center">
                            <p className="text-sm text-gray-500">
                                {gqlQueryVariables || filterContactIds?.[0] || textSearchQuery
                                    ? 'No actions match your filter'
                                    : 'You have no actions to complete'}
                            </p>
                        </motion.div>
                    )}
                    {isFetchingMore && (
                        <div key="fetching-more" className="w-full h-10 bg-gray-200 animate-pulse rounded-md" />
                    )}
                    {!hasMore &&
                        contactsOutsideFilterCount > 0 &&
                        !filterContactIds?.[0] &&
                        !shouldDisplaySyncingCard && (
                            <motion.div
                                key="more-contacts"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, x: -100 }}
                                transition={{
                                    duration: 0.2,
                                    layout: { type: 'spring', stiffness: 300, damping: 30 },
                                }}
                                className="w-full max-w-[min(70dvw,60rem)]"
                                layout="position"
                            >
                                <Card className={cn('w-full flex flex-col gap-2 p-4', className)}>
                                    <div className="flex flex-row gap-2 justify-between items-center text-sm text-muted-foreground">
                                        <span>
                                            <strong>{contactsOutsideFilterCount}</strong> additional contacts without
                                            any prioritized actions
                                        </span>
                                        <Button variant="outline" size="sm" onClick={handleClearFilter}>
                                            Show
                                        </Button>
                                    </div>
                                </Card>
                            </motion.div>
                        )}
                </AnimatePresence>
            </div>
            <SnoozeDialogV2Component />
            <ClassificationDialogComponent />
        </ScrollArea>
    );
};

interface ClassificationDialogProps {
    contactId: string;
    open: boolean;
    close: () => void;
}

export const useClassificationDialog = () => {
    const [classificationDialogOpen, setClassificationDialogOpen] = useState(false);
    const [contactId, setContactId] = useState('');

    return {
        openDialogForContact: (contactId: string) => {
            setContactId(contactId);
            setClassificationDialogOpen(true);
        },
        ClassificationDialogComponent: () => (
            <ClassificationDialog
                contactId={contactId}
                open={classificationDialogOpen}
                close={() => setClassificationDialogOpen(false)}
            />
        ),
    };
};

export const ClassificationDialog: React.FC<ClassificationDialogProps> = ({ contactId, open, close }) => {
    const { data, loading, error } = useQuery(GET_CONTACT_CLASSIFICATION, {
        variables: { id: contactId },
        skip: !contactId,
    });

    // Default weights (should match backend defaults)
    const DEFAULTS = {
        urgent_followup_boost: 1,
        networking_objective_weight: 1,
        sales_objective_weight: 1,
        customer_success_objective_weight: 1,
        fundraising_objective_weight: 1,
        recruiting_objective_weight: 1,
        internal_objective_weight: 1,
        timing_urgency_max_boost: 0.5,
        no_action_penalty: 0.05,
        shelf_base_boost: 1.0,
        shelf_period_days: 14.0,
        very_old_overdue_base_boost: 0.9,
        very_old_decay_rate: 3.0,
        due_today_boost: 0.8,
        due_curve_width: 1.5,
        future_penalty: 0.984,
        future_decay_rate: 0.5,
    };

    const calc = React.useMemo(() => {
        const c = data?.Contacts_by_pk;
        if (!c) return null;
        const metadata = c.metadata || {};
        const objectiveScores = metadata.objectiveScores || {};
        const priorityScore = typeof c.priorityScore === 'number' ? c.priorityScore : 0;

        // Individual components for base_score
        const networking = parseInt(objectiveScores.networking ?? '0', 10);
        const sales = parseInt(objectiveScores.sales ?? '0', 10);
        const customerSuccess = parseInt(objectiveScores.customerSuccess ?? '0', 10);
        const fundraising = parseInt(objectiveScores.fundraising ?? '0', 10);
        const recruiting = parseInt(objectiveScores.recruiting ?? '0', 10);
        const internal = parseInt(objectiveScores.internal ?? '0', 10);

        const networkingComponent = networking * DEFAULTS.networking_objective_weight;
        const salesComponent = sales * DEFAULTS.sales_objective_weight;
        const customerSuccessComponent = customerSuccess * DEFAULTS.customer_success_objective_weight;
        const fundraisingComponent = fundraising * DEFAULTS.fundraising_objective_weight;
        const recruitingComponent = recruiting * DEFAULTS.recruiting_objective_weight;
        const internalComponent = internal * DEFAULTS.internal_objective_weight;

        // 1. base_score (all COALESCE to 0)
        const base_score =
            priorityScore +
            networkingComponent +
            salesComponent +
            customerSuccessComponent +
            fundraisingComponent +
            recruitingComponent +
            internalComponent;

        // 2. timing_urgency_multiplier
        const timingUrgency = metadata.timingUrgency ?? 'NONE';
        let timing_urgency_multiplier = 1.0;
        switch (timingUrgency) {
            case 'IMMEDIATE':
                timing_urgency_multiplier += DEFAULTS.timing_urgency_max_boost;
                break;
            case 'SHORT_TERM':
                timing_urgency_multiplier += DEFAULTS.timing_urgency_max_boost * 0.8;
                break;
            case 'MEDIUM_TERM':
                timing_urgency_multiplier += DEFAULTS.timing_urgency_max_boost * 0.6;
                break;
            case 'LONG_TERM':
                timing_urgency_multiplier += DEFAULTS.timing_urgency_max_boost * 0.4;
                break;
            case 'UNCLEAR':
                timing_urgency_multiplier += DEFAULTS.timing_urgency_max_boost * 0.2;
                break;
            default:
                break;
        }

        // 3. urgent_followup_multiplier
        const urgentFollowUp =
            typeof metadata.urgentFollowUp === 'boolean' ? metadata.urgentFollowUp : metadata.urgentFollowUp === 'true'; // handle string 'true' if present
        const urgent_followup_multiplier = urgentFollowUp ? 1 + DEFAULTS.urgent_followup_boost : 1.0;

        // 4. comprehensive_timing_multiplier
        const effectiveDueDate = c.snoozedUntil ?? c.nextActionSuggestedAt;
        let comprehensive_timing_multiplier = DEFAULTS.no_action_penalty;
        let days_from_effective_due: number | null = null;
        if (effectiveDueDate) {
            const now = new Date();
            const due = new Date(effectiveDueDate);
            days_from_effective_due = Math.round((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

            let shelf = 0;
            if (days_from_effective_due < 0 && days_from_effective_due >= -DEFAULTS.shelf_period_days) {
                shelf =
                    DEFAULTS.shelf_base_boost * (1.0 - Math.abs(days_from_effective_due) / DEFAULTS.shelf_period_days);
            }
            let veryOld = 0;
            if (days_from_effective_due < -DEFAULTS.shelf_period_days) {
                veryOld =
                    -DEFAULTS.very_old_overdue_base_boost *
                    (1.0 -
                        Math.exp(
                            -(Math.abs(days_from_effective_due) - DEFAULTS.shelf_period_days) /
                                DEFAULTS.very_old_decay_rate
                        ));
            }
            const dueToday =
                DEFAULTS.due_today_boost *
                Math.exp(-Math.pow(days_from_effective_due, 2) / (2 * Math.pow(DEFAULTS.due_curve_width, 2)));
            let future = 0;
            if (days_from_effective_due > 0) {
                future =
                    -DEFAULTS.future_penalty * (1.0 - Math.exp(-days_from_effective_due / DEFAULTS.future_decay_rate));
            }
            comprehensive_timing_multiplier = 1.0 + shelf + veryOld + dueToday + future;
        }

        // 5. compositeScore
        const compositeScore =
            base_score * timing_urgency_multiplier * comprehensive_timing_multiplier * urgent_followup_multiplier;

        return {
            base_score,
            timing_urgency_multiplier,
            urgent_followup_multiplier,
            comprehensive_timing_multiplier,
            compositeScore,
            days_from_effective_due,
            effectiveDueDate,
            priorityScore,
            objectiveScores,
            timingUrgency,
            urgentFollowUp,
            // Add breakdown for base_score
            baseScoreBreakdown: {
                priorityScore,
                networking,
                networkingComponent,
                sales,
                salesComponent,
                customerSuccess,
                customerSuccessComponent,
                fundraising,
                fundraisingComponent,
                recruiting,
                recruitingComponent,
                internal,
                internalComponent,
            },
        };
    }, [data]);

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="max-w-[90dvw] max-h-[90dvh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Contact Classification</DialogTitle>
                </DialogHeader>
                <DialogDescription>Introspect classification of this contact.</DialogDescription>
                <div className="flex flex-col gap-4">
                    <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="composite-score-breakdown">
                            <AccordionTrigger>Composite Score Calculation</AccordionTrigger>
                            {calc && (
                                <AccordionContent>
                                    <div className="text-xs text-gray-700">
                                        <div>
                                            <b>Composite Score:</b>{' '}
                                            <span className="text-lg font-bold">{calc.compositeScore.toFixed(2)}</span>
                                        </div>
                                        <div className="mt-2">
                                            <b>Formula:</b> base_score × timing_urgency_multiplier ×
                                            comprehensive_timing_multiplier × urgent_followup_multiplier
                                        </div>
                                        <div className="mt-2">
                                            <b>Base Score:</b> {calc.base_score.toFixed(2)}
                                            <div className="ml-2 mt-1">
                                                <b>Base Score Breakdown:</b>
                                                <ul className="list-disc ml-4">
                                                    <li>priorityScore: {calc.baseScoreBreakdown.priorityScore}</li>
                                                    <li>
                                                        networking: {calc.baseScoreBreakdown.networking} ×{' '}
                                                        {DEFAULTS.networking_objective_weight} ={' '}
                                                        {calc.baseScoreBreakdown.networkingComponent}
                                                    </li>
                                                    <li>
                                                        sales: {calc.baseScoreBreakdown.sales} ×{' '}
                                                        {DEFAULTS.sales_objective_weight} ={' '}
                                                        {calc.baseScoreBreakdown.salesComponent}
                                                    </li>
                                                    <li>
                                                        customerSuccess: {calc.baseScoreBreakdown.customerSuccess} ×{' '}
                                                        {DEFAULTS.customer_success_objective_weight} ={' '}
                                                        {calc.baseScoreBreakdown.customerSuccessComponent}
                                                    </li>
                                                    <li>
                                                        fundraising: {calc.baseScoreBreakdown.fundraising} ×{' '}
                                                        {DEFAULTS.fundraising_objective_weight} ={' '}
                                                        {calc.baseScoreBreakdown.fundraisingComponent}
                                                    </li>
                                                    <li>
                                                        recruiting: {calc.baseScoreBreakdown.recruiting} ×{' '}
                                                        {DEFAULTS.recruiting_objective_weight} ={' '}
                                                        {calc.baseScoreBreakdown.recruitingComponent}
                                                    </li>
                                                    <li>
                                                        internal: {calc.baseScoreBreakdown.internal} ×{' '}
                                                        {DEFAULTS.internal_objective_weight} ={' '}
                                                        {calc.baseScoreBreakdown.internalComponent}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                        <div className="mt-2">
                                            <b>Timing Urgency Multiplier:</b>{' '}
                                            {calc.timing_urgency_multiplier.toFixed(3)} (timingUrgency:{' '}
                                            {calc.timingUrgency})
                                            <div className="ml-2 mt-1">
                                                <b>Timing Urgency Calculation:</b>
                                                <ul className="list-disc ml-4">
                                                    <li>timingUrgency: {calc.timingUrgency}</li>
                                                    <li>
                                                        timing_urgency_max_boost: {DEFAULTS.timing_urgency_max_boost}
                                                    </li>
                                                    <li>Result: 1.0 + boost based on timingUrgency</li>
                                                </ul>
                                            </div>
                                        </div>
                                        <div className="mt-2">
                                            <b>Comprehensive Timing Multiplier:</b>{' '}
                                            {calc.comprehensive_timing_multiplier.toFixed(3)} (days from due:{' '}
                                            {calc.days_from_effective_due !== null
                                                ? calc.days_from_effective_due.toFixed(2)
                                                : 'N/A'}
                                            )
                                            <div className="ml-2 mt-1">
                                                <b>Comprehensive Timing Formula:</b>
                                                <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto">
                                                    {`if (effectiveDueDate == null)
    comprehensive_timing_multiplier = no_action_penalty;
else {
    comprehensive_timing_multiplier = 1.0
        + (shelf_base_boost * (1.0 - (abs(days_from_effective_due) / shelf_period_days))) // if overdue but not very old
        + (-very_old_overdue_base_boost * (1.0 - exp(-(abs(days_from_effective_due) - shelf_period_days) / very_old_decay_rate))) // if very old overdue
        + (due_today_boost * exp(-pow(days_from_effective_due, 2) / (2 * pow(due_curve_width, 2)))) // gaussian boost at due date
        + (-future_penalty * (1.0 - exp(-days_from_effective_due / future_decay_rate))); // if in the future
}`}
                                                </pre>
                                                <b>With values:</b>
                                                <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto">
                                                    {(() => {
                                                        const d = calc.days_from_effective_due;
                                                        if (d === null) {
                                                            return `comprehensive_timing_multiplier = ${DEFAULTS.no_action_penalty}`;
                                                        }
                                                        const shelf =
                                                            d < 0 && d >= -DEFAULTS.shelf_period_days
                                                                ? DEFAULTS.shelf_base_boost *
                                                                  (1.0 - Math.abs(d) / DEFAULTS.shelf_period_days)
                                                                : 0;
                                                        const veryOld =
                                                            d < -DEFAULTS.shelf_period_days
                                                                ? -DEFAULTS.very_old_overdue_base_boost *
                                                                  (1.0 -
                                                                      Math.exp(
                                                                          -(Math.abs(d) - DEFAULTS.shelf_period_days) /
                                                                              DEFAULTS.very_old_decay_rate
                                                                      ))
                                                                : 0;
                                                        const dueToday =
                                                            DEFAULTS.due_today_boost *
                                                            Math.exp(
                                                                -Math.pow(d, 2) /
                                                                    (2 * Math.pow(DEFAULTS.due_curve_width, 2))
                                                            );
                                                        const future =
                                                            d > 0
                                                                ? -DEFAULTS.future_penalty *
                                                                  (1.0 - Math.exp(-d / DEFAULTS.future_decay_rate))
                                                                : 0;
                                                        return `comprehensive_timing_multiplier = 1.0
    + (${DEFAULTS.shelf_base_boost} * (1.0 - (abs(${d.toFixed(2)}) / ${DEFAULTS.shelf_period_days}))) = ${shelf.toFixed(
                                                            3
                                                        )}
    + (${-DEFAULTS.very_old_overdue_base_boost} * (1.0 - exp(-(abs(${d.toFixed(2)}) - ${
                                                            DEFAULTS.shelf_period_days
                                                        }) / ${DEFAULTS.very_old_decay_rate}))) = ${veryOld.toFixed(3)}
    + (${DEFAULTS.due_today_boost} * exp(-pow(${d.toFixed(2)}, 2) / (2 * pow(${
                                                            DEFAULTS.due_curve_width
                                                        }, 2)))) = ${dueToday.toFixed(3)}
    + (${-DEFAULTS.future_penalty} * (1.0 - exp(-${d.toFixed(2)} / ${DEFAULTS.future_decay_rate}))) = ${future.toFixed(
                                                            3
                                                        )}
= ${(1.0 + shelf + veryOld + dueToday + future).toFixed(3)}`;
                                                    })()}
                                                </pre>
                                            </div>
                                        </div>
                                        <div className="mt-2">
                                            <b>Urgent Follow-up Multiplier:</b>{' '}
                                            {calc.urgent_followup_multiplier.toFixed(3)} (urgentFollowUp:{' '}
                                            {String(calc.urgentFollowUp)})
                                            <div className="ml-2 mt-1">
                                                <b>Urgent Follow-up Calculation:</b>
                                                <ul className="list-disc ml-4">
                                                    <li>urgentFollowUp: {String(calc.urgentFollowUp)}</li>
                                                    <li>urgent_followup_boost: {DEFAULTS.urgent_followup_boost}</li>
                                                    <li>
                                                        Result:{' '}
                                                        {calc.urgentFollowUp
                                                            ? `1 + ${DEFAULTS.urgent_followup_boost} = ${calc.urgent_followup_multiplier}`
                                                            : '1.0'}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </AccordionContent>
                            )}
                        </AccordionItem>
                        <AccordionItem value="item-1">
                            <AccordionTrigger>Contact Metadata</AccordionTrigger>
                            {data?.Contacts_by_pk?.metadata && (
                                <AccordionContent>
                                    <div className="overflow-auto max-h-[60dvh] max-w-[80dvw]">
                                        <pre className="break-all">
                                            {JSON.stringify(data.Contacts_by_pk.metadata, null, 2)}
                                        </pre>
                                    </div>
                                </AccordionContent>
                            )}
                        </AccordionItem>
                        <AccordionItem value="item-2">
                            <AccordionTrigger>Actions Metadata</AccordionTrigger>
                            {data?.Contacts_by_pk?.actionsMetadata && (
                                <AccordionContent>
                                    <div className="overflow-auto max-h-[60dvh] max-w-[80dvw]">
                                        <pre className="break-all">
                                            {JSON.stringify(data.Contacts_by_pk.actionsMetadata, null, 2)}
                                        </pre>
                                    </div>
                                </AccordionContent>
                            )}
                        </AccordionItem>
                        <AccordionItem value="item-3">
                            <AccordionTrigger>Conversation Metadata</AccordionTrigger>
                            {data?.Contacts_by_pk?.conversationMetadata && (
                                <AccordionContent>
                                    <div className="overflow-auto max-h-[60dvh] max-w-[80dvw]">
                                        <pre className="break-all">
                                            {JSON.stringify(data.Contacts_by_pk.conversationMetadata, null, 2)}
                                        </pre>
                                    </div>
                                </AccordionContent>
                            )}
                        </AccordionItem>
                        <AccordionItem value="item-4">
                            <AccordionTrigger>Messages Metadata</AccordionTrigger>
                            {data?.Contacts_by_pk?.messagesMetadata && (
                                <AccordionContent>
                                    <div className="overflow-auto max-h-[60dvh] max-w-[80dvw]">
                                        <pre className="break-all">
                                            {JSON.stringify(data.Contacts_by_pk.messagesMetadata, null, 2)}
                                        </pre>
                                    </div>
                                </AccordionContent>
                            )}
                        </AccordionItem>
                        <AccordionItem value="item-5">
                            <AccordionTrigger>Priority Score</AccordionTrigger>
                            {data?.Contacts_by_pk?.priorityScore && (
                                <AccordionContent>
                                    <div className="overflow-auto max-h-[60dvh] max-w-[80dvw]">
                                        <pre className="break-all">
                                            {JSON.stringify(data.Contacts_by_pk.priorityScore, null, 2)}
                                        </pre>
                                    </div>
                                </AccordionContent>
                            )}
                        </AccordionItem>
                    </Accordion>
                </div>
            </DialogContent>
        </Dialog>
    );
};
